import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

/** @format */

/**
 * External dependencies
 */
import { event as d3Event } from 'd3-selection';
import { line as d3Line } from 'd3-shape';
import moment from 'moment';
import { first, get } from 'lodash';
/**
 * Internal dependencies
 */

import { smallBreak, wideBreak } from './breakpoints';
/**
 * Describes getDateSpaces
 * @param {array} data - The chart component's `data` prop.
 * @param {array} uniqueDates - from `getUniqueDates`
 * @param {array} visibleKeys - visible keys from the input data for the chart
 * @param {number} width - calculated width of the charting space
 * @param {function} xScale - from `getXLineScale`
 * @returns {array} that includes the date, start (x position) and width to mode the mouseover rectangles
 */

export var getDateSpaces = function getDateSpaces(data, uniqueDates, visibleKeys, width, xScale) {
  var reversedKeys = visibleKeys.slice().reverse();
  return uniqueDates.map(function (d, i) {
    var datapoints = first(data.filter(function (item) {
      return item.date === d;
    }));
    var xNow = xScale(moment(d).toDate());
    var xPrev = i >= 1 ? xScale(moment(uniqueDates[i - 1]).toDate()) : xScale(moment(uniqueDates[0]).toDate());
    var xNext = i < uniqueDates.length - 1 ? xScale(moment(uniqueDates[i + 1]).toDate()) : xScale(moment(uniqueDates[uniqueDates.length - 1]).toDate());
    var xWidth = i === 0 ? xNext - xNow : xNow - xPrev;
    var xStart = i === 0 ? 0 : xNow - xWidth / 2;
    xWidth = i === 0 || i === uniqueDates.length - 1 ? xWidth / 2 : xWidth;
    return {
      date: d,
      start: uniqueDates.length > 1 ? xStart : 0,
      width: uniqueDates.length > 1 ? xWidth : width,
      values: reversedKeys.map(function (_ref) {
        var key = _ref.key;
        var datapoint = datapoints[key];

        if (!datapoint) {
          return null;
        }

        return {
          key: key,
          value: datapoint.value,
          date: d
        };
      }).filter(Boolean)
    };
  });
};
/**
 * Describes getLine
 * @param {function} xScale - from `getXLineScale`.
 * @param {function} yScale - from `getYScale`.
 * @returns {function} the D3 line function for plotting all category values
 */

export var getLine = function getLine(xScale, yScale) {
  return d3Line().x(function (d) {
    return xScale(moment(d.date).toDate());
  }).y(function (d) {
    return yScale(d.value);
  });
};
/**
 * Describes `getLineData`
 * @param {array} data - The chart component's `data` prop.
 * @param {array} orderedKeys - from `getOrderedKeys`.
 * @returns {array} an array objects with a category `key` and an array of `values` with `date` and `value` properties
 */

export var getLineData = function getLineData(data, orderedKeys) {
  return orderedKeys.map(function (row) {
    return {
      key: row.key,
      focus: row.focus,
      visible: row.visible,
      label: row.label,
      values: data.map(function (d) {
        return {
          date: d.date,
          focus: row.focus,
          value: get(d, [row.key, 'value'], 0),
          visible: row.visible
        };
      })
    };
  });
};
export var drawLines = function drawLines(node, data, params, scales, formats, tooltip) {
  var height = scales.yScale.range()[0];
  var width = scales.xScale.range()[1];
  var line = getLine(scales.xScale, scales.yScale);
  var lineData = getLineData(data, params.visibleKeys);
  var series = node.append('g').attr('class', 'lines').selectAll('.line-g').data(lineData.filter(function (d) {
    return d.visible;
  }).reverse()).enter().append('g').attr('class', 'line-g').attr('role', 'region').attr('aria-label', function (d) {
    return d.label || d.key;
  });
  var dateSpaces = getDateSpaces(data, params.uniqueDates, params.visibleKeys, width, scales.xScale);
  var lineStroke = width <= wideBreak || params.uniqueDates.length > 50 ? 2 : 3;
  lineStroke = width <= smallBreak ? 1.25 : lineStroke;
  var dotRadius = width <= wideBreak ? 4 : 6;
  params.uniqueDates.length > 1 && series.append('path').attr('fill', 'none').attr('stroke-width', lineStroke).attr('stroke-linejoin', 'round').attr('stroke-linecap', 'round').attr('stroke', function (d) {
    return params.getColor(d.key);
  }).style('opacity', function (d) {
    var opacity = d.focus ? 1 : 0.1;
    return d.visible ? opacity : 0;
  }).attr('d', function (d) {
    return line(d.values);
  });
  var minDataPointSpacing = 36;
  width / params.uniqueDates.length > minDataPointSpacing && series.selectAll('circle').data(function (d, i) {
    return d.values.map(function (row) {
      return _objectSpread({}, row, {
        i: i,
        visible: d.visible,
        key: d.key
      });
    });
  }).enter().append('circle').attr('r', dotRadius).attr('fill', function (d) {
    return params.getColor(d.key);
  }).attr('stroke', '#fff').attr('stroke-width', lineStroke + 1).style('opacity', function (d) {
    var opacity = d.focus ? 1 : 0.1;
    return d.visible ? opacity : 0;
  }).attr('cx', function (d) {
    return scales.xScale(moment(d.date).toDate());
  }).attr('cy', function (d) {
    return scales.yScale(d.value);
  }).attr('tabindex', '0').attr('aria-label', function (d) {
    var label = formats.screenReaderFormat(d.date instanceof Date ? d.date : moment(d.date).toDate());
    return "".concat(label, " ").concat(tooltip.valueFormat(d.value));
  }).on('focus', function (d, i, nodes) {
    tooltip.show(data.find(function (e) {
      return e.date === d.date;
    }), nodes[i].parentNode, d3Event.target);
  }).on('blur', function () {
    return tooltip.hide();
  });
  var focus = node.append('g').attr('class', 'focusspaces').selectAll('.focus').data(dateSpaces).enter().append('g').attr('class', 'focus');
  var focusGrid = focus.append('g').attr('class', 'focus-grid').attr('opacity', '0');
  focusGrid.append('line').attr('x1', function (d) {
    return scales.xScale(moment(d.date).toDate());
  }).attr('y1', 0).attr('x2', function (d) {
    return scales.xScale(moment(d.date).toDate());
  }).attr('y2', height);
  focusGrid.selectAll('circle').data(function (d) {
    return d.values;
  }).enter().append('circle').attr('r', dotRadius + 2).attr('fill', function (d) {
    return params.getColor(d.key);
  }).attr('stroke', '#fff').attr('stroke-width', lineStroke + 2).attr('cx', function (d) {
    return scales.xScale(moment(d.date).toDate());
  }).attr('cy', function (d) {
    return scales.yScale(d.value);
  });
  focus.append('rect').attr('class', 'focus-g').attr('x', function (d) {
    return d.start;
  }).attr('y', 0).attr('width', function (d) {
    return d.width;
  }).attr('height', height).attr('opacity', 0).on('mouseover', function (d, i, nodes) {
    var isTooltipLeftAligned = (i === 0 || i === dateSpaces.length - 1) && params.uniqueDates.length > 1;
    var elementWidthRatio = isTooltipLeftAligned ? 0 : 0.5;
    tooltip.show(data.find(function (e) {
      return e.date === d.date;
    }), d3Event.target, nodes[i].parentNode, elementWidthRatio);
  }).on('mouseout', function () {
    return tooltip.hide();
  });
};
//# sourceMappingURL=line-chart.js.map